import Color from '@arcgis/core/Color.js';
import esriConfig from '@arcgis/core/config.js';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import Graphic from '@arcgis/core/Graphic.js';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import ArcGISMap from '@arcgis/core/Map';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import Draw from '@arcgis/core/views/draw/Draw';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import Sketch from '@arcgis/core/widgets/Sketch';
import { ESRI_PLATFORM_API_KEY } from '@env';
import { isArray } from 'lodash';
import { Observable, Subject } from 'rxjs';

import { ViewContainerRef } from '@angular/core';

import { IFsMap } from '@lib/geo/core';
import {
  Evented,
  FsMapCircle,
  FsMapControlsModes,
  FsMapPolygon,
  FsMapPolyline,
  FsMapRectangle,
  FsShapeStyles,
  Maps,
  ShapeStylesType,
  SnLonLat,
  SnLonLatBounds,
  SnLonLatBoundsLike,
  SnLonLatLike,
} from '@lib/geo/helpers';

import { EsriTheme } from '../enum';
import { IEsriMapConstraints, IEsriMapOptions } from '../interfaces';
import { IEsriMapEvents } from './esri-map-events';

import esri = __esri;

esriConfig.apiKey = ESRI_PLATFORM_API_KEY;

export class EsriMap extends Evented<IEsriMapEvents> implements IFsMap {
  public options: IEsriMapOptions;
  public constraints: IEsriMapConstraints;
  public mapStyle: EsriTheme;

  public id: string;

  public readonly viewContainerRef: ViewContainerRef;
  public readonly geospatialProvider = Maps.ESRI;

  public _eventListeners: any[] = [];

  private _map: ArcGISMap;
  private _mapView: MapView;
  private _sceneView: SceneView;
  private _sketch: Sketch;
  private _draw: Draw;
  private _graphicsLayer: GraphicsLayer;

  private readonly _newShape$: Subject<any> = new Subject();

  private readonly _destroy$: Subject<void> = new Subject();
  private _isGoToInProgress = false;
  private _tilesLoadTimeout: number | undefined;

  get mapInstance(): ArcGISMap {
    return this._map;
  }

  get mapView(): MapView {
    return this._mapView;
  }

  get sceneView(): SceneView {
    return this._sceneView;
  }

  get newShape$(): Observable<any> {
    return this._newShape$.asObservable();
  }

  constructor(
    options: IEsriMapOptions,
    constraints: IEsriMapConstraints,
    mapStyle: EsriTheme,
    viewContainerRef: ViewContainerRef,
    id: string,
  ) {
    super();
    this.options = options;
    this.constraints = constraints;
    this.mapStyle = mapStyle;
    this.viewContainerRef = viewContainerRef;
    this.id = id;

    this._initEsriMap(this.options, this.constraints);

    this._initEventListeners();
    this._setupTilesLoadMonitoring();
  }

  public destroy(): void {
    this._destroyEventListeners();

    // Clear tiles load timeout
    if (this._tilesLoadTimeout) {
      window.clearTimeout(this._tilesLoadTimeout);
      this._tilesLoadTimeout = undefined;
    }

    this._map.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  public getBounds(): SnLonLatBounds {
    const latLngBounds = webMercatorUtils.webMercatorToGeographic(
      this._mapView?.extent || this.createExtentFromCenter(),
    );
    const neBound = [latLngBounds.extent.xmax, latLngBounds.extent.ymax];
    const swBound = [latLngBounds.extent.xmin, latLngBounds.extent.ymin];
    return new SnLonLatBounds(swBound, neBound);
  }

  public setNewMapID(mapId: string): void {
    console.warn('setNewMapID is not applicable for Esri maps');
  }

  public getCenter(): SnLonLat {
    const center =
      this._mapView?.center || this._mapView?.extent.center || this._sceneView?.extent.center;
    return new SnLonLat(center.longitude, center.latitude);
  }

  public getZoom(): number {
    return this._mapView?.zoom || this._sceneView.zoom;
  }

  public getHeading(): number {
    return this._mapView?.rotation || this._sceneView.camera.heading;
  }

  public createExtentFromCenter(): Extent {
    const center = this._mapView.center;
    const zoom = this._mapView.zoom;

    const resolution = this._mapView.resolution;

    const mapWidthInPixels = this._mapView.width;
    const mapHeightInPixels = this._mapView.height;

    const extentWidth = mapWidthInPixels * resolution;
    const extentHeight = mapHeightInPixels * resolution;

    const xmin = center.x - extentWidth / 2;
    const ymin = center.y - extentHeight / 2;
    const xmax = center.x + extentWidth / 2;
    const ymax = center.y + extentHeight / 2;

    return new Extent({
      xmin: xmin,
      ymin: ymin,
      xmax: xmax,
      ymax: ymax,
      spatialReference: { wkid: 4326 },
    });
  }

  public async panTo(target: any, options?: any): Promise<void> {
    this._isGoToInProgress = true;

    try {
      return await this._mapView.goTo(target, options);
    } finally {
      this._isGoToInProgress = false;
    }
  }

  public panToBounds(_: SnLonLatBoundsLike): this {
    console.warn('panToBounds not implemented');
    return this
  }

  public setCenter(lonLatLike: SnLonLatLike | number[]): void {
    if (!this._mapView) {
      return;
    }

    let lon: number, lat: number;

    if (Array.isArray(lonLatLike)) {
      [lon, lat] = lonLatLike;
    } else {
      const lonLat = SnLonLat.convert(lonLatLike);
      lon = lonLat.lon;
      lat = lonLat.lat;
    }

    this._mapView.center = new Point({
      longitude: lon,
      latitude: lat,
    });
  }

  public async setCenterWithAnimation(lonLatLike: SnLonLatLike | number[]): Promise<void> {
    if (!this._mapView) {
      return;
    }

    await this._waitForGoToComplete();

    let lon: number, lat: number;

    if (Array.isArray(lonLatLike)) {
      [lon, lat] = lonLatLike;
    } else {
      const lonLat = SnLonLat.convert(lonLatLike);
      lon = lonLat.lon;
      lat = lonLat.lat;
    }

    await this.panTo({
      center: [lon, lat],
    });
  }

  public setZoom(zoom: number): void {
    if (!this._mapView) {
      return;
    }

    this._mapView.zoom = zoom;
  }

  public async setZoomWithAnimation(zoom: number): Promise<void> {
    if (!this._mapView) {
      return;
    }

    await this._waitForGoToComplete();

    await this.panTo({
      zoom,
    });
  }

  public setCenterSmart(
    lonLatLike: SnLonLatLike | number[],
    animated: boolean = true,
  ): Promise<void> | void {
    if (animated) {
      return this.setCenterWithAnimation(lonLatLike);
    } else {
      this.setCenter(lonLatLike);
    }
  }

  public setStyle(style: string): this {
    const formattedStyle: string = this._formatStyle(style);
    if (this._mapView) {
      this._map.basemap = formattedStyle as any;
    } else if (this._sceneView) {
      this._sceneView.map = new ArcGISMap({
        basemap: formattedStyle,
      });
    }
    return this;
  }

  public setOptions(options: IEsriMapOptions): this {
    if (options.center) {
      this.setCenterSmart(options.center, options.animated);
    }

    if (options.zoom !== undefined) {
      this.setZoom(options.zoom);
    }

    if (options.tilt !== undefined && this._sceneView) {
      this._sceneView.camera.tilt = options.tilt;
    }

    if (options.rotation !== undefined) {
      if (this._mapView) {
        this._mapView.rotation = options.rotation;
      } else if (this._sceneView) {
        this._sceneView.camera.heading = options.rotation;
      }
    }

    if (options.scale !== undefined && this._mapView) {
      this._mapView.scale = options.scale;
    }

    return this;
  }

  public getStyle(): string {
    return this._formatStyle(this.mapStyle);
  }

  public contains(lonLatLike: SnLonLatLike): boolean {
    const lonLat = SnLonLat.convert(lonLatLike);
    const latLngBounds = webMercatorUtils.webMercatorToGeographic(
      this._mapView?.extent || this._sceneView?.extent,
    );
    const point = new Point({
      latitude: lonLat.lat,
      longitude: lonLat.lon,
    });
    return latLngBounds.extent.contains(point);
  }

  public emitDrawing(
    tool: 'circle' | 'point' | 'polygon' | 'polyline' | 'rectangle',
    mode: FsMapControlsModes,
    style?: ShapeStylesType,
  ): void {
    style ? this._updateSketchStyle(style) : this._revertSketchStyle();
    this._sketch.create(tool);
    this._sketch.on('create', (event) => {
      if (event.state === 'complete') {
        this._createShape(tool, event.graphic, mode);
      }
    });
    this._resetSketch();
  }

  public getContainer(): HTMLElement {
    return document.getElementById(`esri-map-${this.id}`);
  }

  public fitBounds(bounds: SnLonLatBounds): this {
    const extent = SnLonLatBounds.convertToExtent(bounds);
    this._mapView.goTo({
      target: extent,
    });
    return this;
  }

  public takeScreenshot(): any {
    return this._mapView.takeScreenshot();
  }

  public freehand(): void {
    this._draw = new Draw({
      view: this._mapView,
    });

    const action = this._draw.create('polyline', { mode: 'freehand' });
    action.on('draw-complete', (e) => {
      const polygon = this._createPolylineGraphic(e.vertices);
      this._newShape$.next(polygon);
    });

    action.on('vertex-add', (evt) => {
      this._createPolylineGraphic(evt.vertices);
    });
  }

  setMapId(mapId: string): void {
    // Esri maps don't use map IDs like Google Maps
    console.warn('setMapId is not applicable for Esri maps');
  }

  addLayer(layer: any, index?: number): void {
    if (this._mapView) {
      this._mapView.map.add(layer, index);
    }
    if (this._sceneView) {
      this._sceneView.map.add(layer, index);
    }
  }

  removeLayer(layer: any): void {
    if (this._mapView) {
      this._mapView.map.remove(layer);
    }
    if (this._sceneView) {
      this._sceneView.map.remove(layer);
    }
  }

  removeLayers(): void {
    if (this._mapView) {
      this._mapView.map.layers.removeAll();
    }
    if (this._sceneView) {
      this._sceneView.map.layers.removeAll();
    }
  }

  setHeading(heading: number): void {
    if (this._mapView) {
      this._mapView.rotation = heading;
    }
    if (this._sceneView) {
      this._sceneView.camera.heading = heading;
    }
  }

  public addImageOverlay(overlay: any, index?: number): void {
    this._mapView.map.layers.add(overlay, index || 0);
  }

  public addShape(shape: any, index?: number): void {
    this._mapView.graphics.add(shape, index || 0);
  }

  public removeShape(shape: any): void {
    this._mapView.graphics.remove(shape);
  }

  public removeImageLayer(overlay: any): void {
    this._mapView.map.layers.remove(overlay);
  }

  public addMarker(marker: esri.Graphic[]): void {
    if (marker.length > 1) {
      this._mapView
        ? this._mapView.graphics.addMany(marker)
        : this._sceneView.graphics.addMany(marker);
    } else {
      this._mapView
        ? this._mapView.graphics.add(marker[0])
        : this._sceneView.graphics.add(marker[0]);
    }
  }

  public removeMarker(marker: esri.Graphic[] | esri.Graphic): void {
    if (!marker) {
      return;
    }
    if (isArray(marker)) {
      this._mapView
        ? this._mapView.graphics.removeMany(marker)
        : this._sceneView.graphics.removeMany(marker);
    } else {
      this._mapView
        ? this._mapView.graphics.remove(marker)
        : this._sceneView.graphics.remove(marker);
    }
  }

  private _waitForGoToComplete(): Promise<void> {
    return new Promise((resolve) => {
      const checkGoToCompletion = () => {
        if (!this._isGoToInProgress) {
          resolve();
        } else {
          requestAnimationFrame(checkGoToCompletion);
        }
      };
      checkGoToCompletion();
    });
  }

  private _initEsriMap(options: IEsriMapOptions, constraints: IEsriMapConstraints): ArcGISMap {
    this._map = new ArcGISMap({
      basemap: this.mapStyle || EsriTheme.Dark,
    });

    this._mapView = new MapView({
      map: this._map,
      container: `esri-map-${this.id}`,
      ...options,
      constraints: {
        rotationEnabled: false,
        ...constraints,
      },
      popup: {
        dockOptions: {
          buttonEnabled: false,
        },
      },
      ui: {
        components: [],
      },
    });

    this._graphicsLayer = new GraphicsLayer();

    this._resetSketch();

    return this._map;
  }

  private _initEsri3dMap(options: IEsriMapOptions, constraints: IEsriMapConstraints): ArcGISMap {
    const map = new ArcGISMap({
      basemap: this.mapStyle || EsriTheme.Streets,
    });

    this._sceneView = new SceneView({
      map,
      container: `esri-map-${this.id}`,
      camera: {
        position: {
          x: options.center[0],
          y: options.center[1],
          z: options.center[2] || 100,
        },
        tilt: options.tilt || 45,
      },
    });

    return map;
  }

  private _initEventListeners(): void {
    const eventListeners = [
      this.mapView?.on('immediate-click', this._onClick.bind(this)),
      this.mapView?.on('immediate-double-click', this._onDblClick.bind(this)),
      this.mapView?.on('drag', this._onDrag.bind(this)),
      this.mapView?.watch('center', this._onCenterChanged.bind(this)),
      this.mapView?.watch('extent', this._onBoundsChanged.bind(this)),
      this.mapView?.watch('zoom', this._onZoomChanged.bind(this)),
      this.mapView?.watch('stationary', this._onIdle.bind(this)),
      this.mapView?.watch('map', this._onStyleChanged.bind(this)),
      this.sceneView?.on('immediate-click', this._onClick.bind(this)),
      this.sceneView?.on('immediate-double-click', this._onDblClick.bind(this)),
      this.sceneView?.on('drag', this._onDrag.bind(this)),
      this.sceneView?.watch('center', this._onCenterChanged.bind(this)),
      this.sceneView?.watch('extent', this._onBoundsChanged.bind(this)),
      this.sceneView?.watch('zoom', this._onZoomChanged.bind(this)),
      this.sceneView?.watch('stationary', this._onIdle.bind(this)),
    ];

    this._eventListeners.push(...eventListeners);
  }

  private _destroyEventListeners(): void {
    this._eventListeners.forEach((event) => event?.remove());
  }

  private _setupTilesLoadMonitoring(): void {
    const timeoutMs = 12000;

    // ESRI doesn't have a direct 'tilesloaded' event, so we use 'stationary' as the closest equivalent
    // The 'stationary' property becomes true when the map has finished loading and rendering
    const onStationary = this._mapView?.watch('stationary', (isStationary) => {
      if (isStationary && this._tilesLoadTimeout) {
        window.clearTimeout(this._tilesLoadTimeout);
        this._tilesLoadTimeout = undefined;
        this.fire('tilesLoaded');
        onStationary?.remove();
      }
    });

    // Also handle SceneView if it exists
    const onSceneStationary = this._sceneView?.watch('stationary', (isStationary) => {
      if (isStationary && this._tilesLoadTimeout) {
        window.clearTimeout(this._tilesLoadTimeout);
        this._tilesLoadTimeout = undefined;
        this.fire('tilesLoaded');
        onSceneStationary?.remove();
      }
    });

    // Set up timeout for tiles load error
    this._tilesLoadTimeout = window.setTimeout(() => {
      this.fire('tilesLoadError', { timeoutMs });
    }, timeoutMs);
  }

  private _onClick(esriEvent: esri.ViewImmediateClickEvent): void {
    const lonLat = new SnLonLat(esriEvent.mapPoint.longitude, esriEvent.mapPoint.latitude);
    this.fire('click', lonLat);
  }

  private _onDblClick(esriEvent: esri.ViewImmediateDoubleClickEvent): void {
    const lonLat = new SnLonLat(esriEvent.mapPoint.longitude, esriEvent.mapPoint.latitude);
    this.fire('dblclick', lonLat);
  }

  private _onDrag(esriEvent: esri.ViewDragEvent): void {
    switch (esriEvent.action) {
      case 'start':
        this.fire('dragstart');
        break;
      case 'update':
        this.fire('drag');
        break;
      case 'end':
        this.fire('dragend');
        break;
    }
  }

  private _onIdle(): void {
    this.fire('idle');
  }

  private _onCenterChanged(): void {
    this.fire('centerChange');
  }

  private _onBoundsChanged(): void {
    this.fire('boundsChange');
  }

  private _onZoomChanged(): void {
    this.fire('zoomChange', this.getZoom());
  }

  private _onStyleChanged(): void {
    this.fire('styleChange');
  }

  private _formatStyle(style: string): string {
    switch (style.toLowerCase()) {
      case 'dark':
        return EsriTheme.Dark;
      case 'light':
        return EsriTheme.Light;
      case 'satellite':
        return EsriTheme.Streets;
      default:
        return EsriTheme.Dark;
    }
  }

  private _createPolylineGraphic(vertices: any): Graphic {
    this._mapView.graphics.removeAll();
    const polyline: any = {
      type: 'polyline',
      paths: vertices,
      spatialReference: this._mapView.spatialReference,
    };

    const simpleFill = new SimpleFillSymbol({
      style: 'solid',
      color: new Color('rgba(102, 189, 77, 0.25)'),
      outline: {
        color: new Color('rgba(102, 189, 77, 0.25)'),
        width: 1,
        style: 'solid',
        join: 'miter',
      },
    });

    const polygon = new Polygon({
      spatialReference: {
        wkid: 4326,
      },
      rings: polyline.paths,
    });

    const graphic = new Graphic({
      geometry: polyline,
      symbol: simpleFill,
    });
    this._mapView.graphics.add(graphic);

    return graphic;
  }

  private _updateSketchStyle(style: ShapeStylesType): void {
    const styles: any = new FsShapeStyles({ ...style }).styles;
    this._sketch.viewModel.polygonSymbol = new SimpleFillSymbol({
      ...styles,
    });
  }

  private _revertSketchStyle(): void {
    this._sketch.viewModel.polygonSymbol = new SimpleFillSymbol({
      style: 'solid',
      color: new Color('rgba(102, 189, 77, 0.25)'),
      outline: {
        color: new Color('rgba(102, 189, 77, 0.25)'),
        width: 1,
        style: 'solid',
        join: 'miter',
      },
    });
  }

  private _createShape(
    tool: 'circle' | 'point' | 'polygon' | 'polyline' | 'rectangle',
    shape: any,
    mode: FsMapControlsModes,
  ): void {
    switch (tool) {
      case 'circle':
        const circle = new FsMapCircle(shape);
        this._newShape$.next({
          shape: circle,
          mode,
        });
        break;
      case 'point':
        break;
      case 'polygon':
        const polygon = new FsMapPolygon(shape);
        this._newShape$.next({
          shape: polygon,
          mode,
        });
        break;
      case 'rectangle':
        const rectangle = new FsMapRectangle(shape);
        this._newShape$.next({
          shape: rectangle,
          mode,
        });
        break;
      case 'polyline':
        const polyline = new FsMapPolyline(shape);
        this._newShape$.next({
          shape: polyline,
          mode,
        });
        break;
    }
  }

  private _resetSketch(): void {
    this._sketch = new Sketch({
      layer: this._graphicsLayer,
      view: this._mapView,
      visibleElements: {
        createTools: {
          polyline: false,
          point: false,
        },
        settingsMenu: false,
        undoRedoMenu: false,
        selectionTools: {
          'lasso-selection': false,
          'rectangle-selection': false,
        },
      },
      creationMode: 'single',
    });

    this._revertSketchStyle();

    this.mapView.ui.add(this._sketch, 'hidden');
  }

  private _googleToEsriZoom(googleZoom) {
    return Math.round(googleZoom * (21 / 24));
  }
}
